import React from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Settings, BarChart3, Shield, CheckCircle, ArrowLeft } from 'lucide-react';

export default function CookiePolicy() {
  const lastUpdated = "November 16, 2025";

  return (
    <article className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-blue-100 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center mb-6">
            <Cookie className="h-12 w-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">Cookie Policy</h1>
          </div>
          <p className="text-xl text-blue-100">
            Last updated: {lastUpdated}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-8 rounded-r-lg">
          <p className="text-gray-700 leading-relaxed">
            This Cookie Policy explains how CyberEdge uses cookies and similar technologies to recognize you when you visit our website. It explains what these technologies are and why we use them, as well as your rights to control our use of them.
          </p>
        </div>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <Cookie className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">What Are Cookies?</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
            </p>
            <p className="mb-4">
              Cookies set by the website owner (in this case, CyberEdge) are called "first-party cookies." Cookies set by parties other than the website owner are called "third-party cookies." Third-party cookies enable third-party features or functionality to be provided on or through the website.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Types of Cookies We Use</h2>
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Essential Cookies</h3>
                  <p className="text-gray-700 mb-3">
                    These cookies are strictly necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you which amount to a request for services.
                  </p>
                  <p className="text-sm text-gray-600 font-medium">Examples:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                    <li>Authentication and security cookies</li>
                    <li>Session state cookies</li>
                    <li>Shopping cart cookies</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start">
                <BarChart3 className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Analytics and Performance Cookies</h3>
                  <p className="text-gray-700 mb-3">
                    These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us know which pages are the most and least popular and see how visitors move around the site.
                  </p>
                  <p className="text-sm text-gray-600 font-medium">Examples:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                    <li>Google Analytics cookies</li>
                    <li>Page view tracking</li>
                    <li>User engagement metrics</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start">
                <Settings className="h-6 w-6 text-orange-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Functionality Cookies</h3>
                  <p className="text-gray-700 mb-3">
                    These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages.
                  </p>
                  <p className="text-sm text-gray-600 font-medium">Examples:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                    <li>Language preference cookies</li>
                    <li>Theme selection cookies</li>
                    <li>Form auto-fill cookies</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start">
                <Shield className="h-6 w-6 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Targeting and Advertising Cookies</h3>
                  <p className="text-gray-700 mb-3">
                    These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant ads on other sites.
                  </p>
                  <p className="text-sm text-gray-600 font-medium">Examples:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                    <li>Retargeting cookies</li>
                    <li>Ad impression tracking</li>
                    <li>Conversion tracking pixels</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Specific Cookies We Use</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white shadow-sm rounded-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Cookie Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Purpose</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">session_id</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Maintains user session</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Session</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">user_preferences</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Stores user settings</td>
                  <td className="px-6 py-4 text-sm text-gray-700">1 year</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">_ga</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Google Analytics - distinguishes users</td>
                  <td className="px-6 py-4 text-sm text-gray-700">2 years</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">_gid</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Google Analytics - distinguishes users</td>
                  <td className="px-6 py-4 text-sm text-gray-700">24 hours</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">cookie_consent</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Stores cookie consent preferences</td>
                  <td className="px-6 py-4 text-sm text-gray-700">1 year</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <Settings className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">How to Control Cookies</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              You have the right to decide whether to accept or reject cookies. You can exercise your cookie rights by setting your preferences in the Cookie Consent Manager that appears when you first visit our website.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Browser Controls</h3>
            <p className="mb-4">
              Most web browsers allow you to control cookies through their settings preferences. However, if you limit the ability of websites to set cookies, you may worsen your overall user experience.
            </p>

            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-4">
              <p className="font-semibold mb-3">To manage cookies in popular browsers:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and site data</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Opt-Out Links</h3>
            <p className="mb-4">
              You can opt out of targeted advertising cookies through these industry opt-out pages:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Digital Advertising Alliance: <a href="http://www.aboutads.info/choices/" className="text-blue-600 hover:text-blue-800">www.aboutads.info/choices/</a></li>
              <li>Network Advertising Initiative: <a href="http://www.networkadvertising.org/choices/" className="text-blue-600 hover:text-blue-800">www.networkadvertising.org/choices/</a></li>
              <li>European Interactive Digital Advertising Alliance: <a href="http://www.youronlinechoices.eu/" className="text-blue-600 hover:text-blue-800">www.youronlinechoices.eu/</a></li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Other Tracking Technologies</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              In addition to cookies, we may use other tracking technologies such as:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Web Beacons:</strong> Small graphic images (also known as "pixel tags" or "clear GIFs") that may be included on our website and emails to track user behavior</li>
              <li><strong>Local Storage:</strong> HTML5 local storage that allows us to store data locally in your browser</li>
              <li><strong>Session Storage:</strong> Similar to local storage but data is cleared when the browser is closed</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Do Not Track Signals</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              Some browsers have a "Do Not Track" feature that lets you tell websites that you do not want to have your online activities tracked. At this time, we do not respond to browser "Do Not Track" signals, but we do provide you with the ability to manage your cookie preferences as described above.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Updates to This Cookie Policy</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. Please revisit this Cookie Policy regularly to stay informed about our use of cookies.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              If you have any questions about our use of cookies or this Cookie Policy, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <p className="mb-2"><strong>CyberEdge Solutions</strong></p>
              <p className="mb-2">Email: info@cyberedgesolutions.com</p>
              <p className="mb-2">Phone: +91 6301644590</p>
              <p>Address: PJR Arcade, Kukatpally, Hyderabad, Telangana 500085</p>
            </div>
          </div>
        </section>

        <div className="flex flex-wrap gap-4 pt-8 border-t border-gray-200">
          <Link
            to="/privacy"
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Terms of Service
          </Link>
          <Link
            to="/gdpr"
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            GDPR Compliance
          </Link>
        </div>
      </div>
    </article>
  );
}
